import React, { FC, useState } from 'react';
import styled from 'styled-components';
import ChecklistComplete from '../ChecklistComplete/ChecklistComplete';
import ChecklistInProgress from '../ChecklistInProgress/ChecklistInProgress';
import { useMutation, useQuery } from '@apollo/client';
import { CURRENT_ORGANIZATION_ONBOARDING, SAVE_ONBOARDING_STEP } from '@lib/queries';
import { AlertType, useAlertContext } from '@lib/alertContext';
import {
  CurrentOrganizationOnboardingQuery,
  CurrentOrganizationOnboardingQueryVariables,
  SaveOnboardingStepMutation,
} from '@generated/types';

interface ShowWrapperProps {
  show: boolean;
  delayedTransition: boolean;
}

const ShowWrapper = styled.div<ShowWrapperProps>`
  visibility: ${props => (props.show ? 'visible;' : 'hidden;')};
  opacity: ${props => (props.show ? '1' : '0')};
  transform: ${props => (props.show ? 'scale(1)' : 'scale(1.05)')};
  transition: all 300ms;
  ${props => (props.delayedTransition ? 'transition-delay: 550ms;' : 'transition-delay: 250ms;')}
  grid-row-start: 1;
  grid-column-start: 1;
`;

interface WrapperProps {
  visible: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  display: ${props => (props.visible ? 'grid' : 'none')};
  border-radius: 4px;
  background-color: white;
  padding-top: 12px;
  grid-template-columns: 1fr;
`;

const Checklist: FC = () => {
  const { openAlert } = useAlertContext();
  const {
    data,
    loading: loadingSteps,
    previousData,
  } = useQuery<CurrentOrganizationOnboardingQuery, CurrentOrganizationOnboardingQueryVariables>(
    CURRENT_ORGANIZATION_ONBOARDING
  );

  const [hasClickedExit, setHasClickedExit] = useState(false);

  const completed =
    data?.currentOrganization?.onboardingSteps?.every(step => step.completed || step.skipped) ||
    false;
  const hasSteps = (data?.currentOrganization?.onboardingSteps?.length ?? 0) > 0;

  // If there is no previousData, we know they are not refetching a query after saving an onboarding step
  const firstLoad = !previousData;

  // (!completed || !firstLoad) so we show the component when the org completes the final step but we don't
  // show the component when the org has already completed them upon loading
  const showComponent = !hasClickedExit && !loadingSteps && hasSteps && (!completed || !firstLoad);

  const [saveOnboardingStep] = useMutation<SaveOnboardingStepMutation>(SAVE_ONBOARDING_STEP, {
    onCompleted: data => {
      if (data.saveOnboardingStep?.errors.length !== 0) {
        openAlert(AlertType.ERROR, 'An error occurred.');
      }
    },
    onError: () => openAlert(AlertType.ERROR, 'An error occurred.'),
    refetchQueries: [
      {
        query: CURRENT_ORGANIZATION_ONBOARDING,
      },
    ],
  });

  const skipStep = (stepId: string): void => {
    saveOnboardingStep({
      variables: {
        input: {
          id: stepId,
          skipped: true,
        },
      },
    });
  };

  return (
    <Wrapper visible={showComponent}>
      <ShowWrapper delayedTransition={true} show={completed}>
        <ChecklistComplete
          closeClicked={() => setHasClickedExit(true)}
          testId='checklist-complete'
        />
      </ShowWrapper>
      <ShowWrapper delayedTransition={false} show={!completed}>
        <ChecklistInProgress
          onboardingSteps={data?.currentOrganization?.onboardingSteps || []}
          skipOnboardingStep={skipStep}
          testId='checklist-steps'
        />
      </ShowWrapper>
    </Wrapper>
  );
};

export default Checklist;
